import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

const CATEGORIES = [
  { key: "fantasy", label: "Fantasy" },
  { key: "passing", label: "Passing" },
  { key: "rushing", label: "Rushing" },
  { key: "receiving", label: "Receiving" },
  { key: "defense", label: "Defense" },
  { key: "kicking", label: "Kicking" },
];

const POSITIONS_BY_CATEGORY = {
  fantasy: ["All", "QB", "RB", "WR", "TE", "PK", "LB", "S", "CB", "DE"],
  passing: ["All", "QB", "RB", "WR", "TE", "FB"],
  rushing: ["All", "RB", "QB", "WR", "TE", "FB"],
  receiving: ["All", "WR", "TE", "RB", "FB"],
  defense: ["All", "LB", "S", "CB", "DE", "DT", "DL", "DB"],
  kicking: ["All", "PK", "K"],
};

function PlayerStatsPage() {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("passing");
  const [selectedPosition, setSelectedPosition] = useState("All");
  const [selectedConference, setSelectedConference] = useState("All");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fantasyPlayers, setFantasyPlayers] = useState([]);
  const [selectedFantasyFormat, setSelectedFantasyFormat] = useState("full_ppr");
  const [selectedSeason, setSelectedSeason] = useState(2026);
  const [sortConfig, setSortConfig] = useState({
    key: "passing_yards",
    direction: "desc",
  });
  const [loading, setLoading] = useState(true);

  // Navbar state (unchanged)
  const [openDropdown, setOpenDropdown] = useState(null);
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

  // Click outside handlers (navbar) — same as before
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navbarContainerRef.current && !navbarContainerRef.current.contains(e.target)) {
        setSearchExpanded(false);
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
      if (
        teamsRef.current && !teamsRef.current.contains(e.target) &&
        leadersRef.current && !leadersRef.current.contains(e.target) &&
        playersRef.current && !playersRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navbar search
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }
    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(navbarSearchTerm)}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };
    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navbarContainerRef.current && !navbarContainerRef.current.contains(e.target)) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!navbarResults.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setNavbarSelectedIndex((prev) => Math.min(prev + 1, navbarResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
        const selected = navbarResults[navbarSelectedIndex];
        const path = selected.type === "team" ? `/team/${selected.abbreviation}` : `/player/${selected.bdl_id}`;
        window.location.href = path;
        setNavbarSearchTerm("");
        setNavbarResults([]);
      } else if (e.key === "Escape") {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navbarResults, navbarSelectedIndex]);
 
  // Fetch data
      useEffect(() => {
      const fetchStats = async () => {
        try {
          const res = await axios.get(
            `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-player-stats-table?season=${selectedSeason}`,
            AUTH
          );
          setPlayers(res.data);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchStats();
    }, [selectedSeason]);

    useEffect(() => {
      const fetchFantasy = async () => {
        try {
          const res = await axios.get(
            `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-fantasy-season-stats?season=${selectedSeason}`,
            AUTH
          );
          setFantasyPlayers(res.data);
        } catch (err) {
          console.error("Fantasy stats error:", err);
        }
      };
      fetchFantasy();
    }, [selectedSeason]);

  // Default sort key per category
  const getDefaultSortKey = (cat) => {
    if (cat === "fantasy") {
    const prefix = selectedFantasyFormat === "full_ppr" ? "full_ppr" : 
                   selectedFantasyFormat === "half_ppr" ? "half_ppr" : "standard";
    return `${prefix}_total`;
  }
  const map = {
    fantasy: "full_ppr_total",
    passing: "passing_yards",
    rushing: "rushing_yards",
    receiving: "receiving_yards",
    defense: "total_tackles",
    kicking: "field_goals_made",
  };
  return map[cat] || "passing_yards";
};

useEffect(() => {
  if (selectedCategory === "fantasy") {
    setSortConfig({ key: getDefaultSortKey("fantasy"), direction: "desc" });
  }
}, [selectedFantasyFormat]);

  // Reset sort + position when category changes
  useEffect(() => {
    setSortConfig({ key: getDefaultSortKey(selectedCategory), direction: "desc" });
    setSelectedPosition("All");
  }, [selectedCategory]);

  // Filter + sort
  useEffect(() => {
  let result = selectedCategory === "fantasy" 
    ? fantasyPlayers.map(fp => {
      if (fantasyPlayers.length > 0 && players.length > 0) {
      console.log("FANTASY SAMPLE", fantasyPlayers[0]);
      console.log("REGULAR SAMPLE", players[0]);
    }
        const regular = players.find(p => p.player_id === fp.player_id || p.bdl_id === fp.player_id);
        return { ...regular, ...fp }; // fantasy fields override, regular stats fill the rest
      })
    : [...players];

  if (selectedPosition !== "All") {
    result = result.filter((p) => p.position === selectedPosition);
  }
  if (selectedConference !== "All") {
  result = result.filter((p) => p.conference === selectedConference);
  }

  const sortKey = sortConfig.key;
  result.sort((a, b) => {
    const aVal = a[sortKey] ?? 0;
    const bVal = b[sortKey] ?? 0;
    return sortConfig.direction === "desc" ? bVal - aVal : aVal - bVal;
  });

  setFilteredPlayers(result);
}, [selectedCategory, selectedPosition, selectedConference, players, fantasyPlayers, sortConfig, selectedFantasyFormat]);

    
  const formatValue = (val) => {
  if (val == null || val === "" || val === undefined) return 0;
  const num = Number(val);
  if (Number.isNaN(num)) return val;
  // integers stay as integers, everything else max 2 decimals
  return Math.abs(num % 1) < 0.00001 ? num : num.toFixed(2);
};


  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

const getColumns = (category, advanced) => {
  const base = [
    { key: "rank", label: "#", sortable: false },
    { key: "name", label: "Player", sortable: true },
    { key: "team", label: "Team", sortable: true },
    { key: "position", label: "Pos", sortable: true },
  ];

  let stats = [];

  if (!advanced) {
    // ===== BASIC =====
    if (category === "passing") {
      stats = [
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
        { key: "passing_interceptions", label: "INT", sortable: true },
        { key: "passing_completion_pct", label: "Comp %", sortable: true },
        { key: "qbr", label: "QBR", sortable: true },
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
      ];
    } else if (category === "rushing") {
      stats = [
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "rushing_fumbles_lost", label: "Fum Lost", sortable: true },
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
      ];
    } else if (category === "receiving") {
      stats = [
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
        { key: "receiving_targets", label: "Targets", sortable: true },
        { key: "receiving_yards_per_game", label: "Yds/G", sortable: true },
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
      ];
    } else if (category === "defense") {
      stats = [
        { key: "total_tackles", label: "Tackles", sortable: true },
        { key: "solo_tackles", label: "Solo", sortable: true },
        { key: "assist_tackles", label: "Assisted", sortable: true },
        { key: "defensive_sacks", label: "Sacks", sortable: true },
        { key: "defensive_sack_yards", label: "Sack Yds", sortable: true },
        { key: "fumbles_forced", label: "Forced Fum", sortable: true },
        { key: "fumbles_recovered", label: "Fum Rec", sortable: true },
        { key: "fumbles_touchdowns", label: "Fum TDs", sortable: true },
        { key: "defensive_interceptions", label: "INT", sortable: true },
        { key: "interception_touchdowns", label: "INT TDs", sortable: true },
      ];
    } else if (category === "kicking") {
      stats = [
        { key: "field_goals_made", label: "FG Made", sortable: true },
        { key: "field_goal_attempts", label: "FG Att", sortable: true },
        { key: "field_goal_pct", label: "FG %", sortable: true },
        { key: "field_goals_made_1_19", label: "FGM 1-19", sortable: true },
        { key: "field_goals_made_20_29", label: "FGM 20-29", sortable: true },
        { key: "field_goals_made_30_39", label: "FGM 30-39", sortable: true },
        { key: "field_goals_made_40_49", label: "FGM 40-49", sortable: true },
        { key: "field_goals_made_50", label: "FGM 50+", sortable: true },
        { key: "field_goals_attempts_20_29", label: "FGA 20-29", sortable: true },
        { key: "field_goals_attempts_30_39", label: "FGA 30-39", sortable: true },
        { key: "field_goals_attempts_40_49", label: "FGA 40-49", sortable: true },
        { key: "field_goals_attempts_50", label: "FGA 50+", sortable: true },
      ];
       } else if (category === "fantasy") {
  const format = selectedFantasyFormat;
  const prefix = format === "full_ppr" ? "full_ppr" : format === "half_ppr" ? "half_ppr" : "standard";
  const labelPrefix = format === "full_ppr" ? "PPR" : format === "half_ppr" ? "Half PPR" : "Std";

  stats = [
    { key: "games_played", label: "GP", sortable: true },
    { key: `${prefix}_total`, label: `${labelPrefix} Total`, sortable: true },
    { key: `${prefix}_avg`, label: `${labelPrefix} Avg`, sortable: true },
    { key: `${prefix}_high`, label: `${labelPrefix} High`, sortable: true },
    { key: `${prefix}_low`, label: `${labelPrefix} Low`, sortable: true },
  ];

  // All → QB/passing columns; otherwise use the selected position
  const posForColumns = selectedPosition === "All" ? "QB" : selectedPosition;

  const categoryForPos =
    posForColumns === "QB" ? "passing" :
    posForColumns === "RB" || posForColumns === "FB" ? "rushing" :
    posForColumns === "WR" || posForColumns === "TE" ? "receiving" :
    ["LB", "S", "CB", "DE", "DT", "DL", "DB"].includes(posForColumns) ? "defense" :
    posForColumns === "PK" || posForColumns === "K" ? "kicking" :
    "passing";

  const extra = getColumns(categoryForPos, advanced)
    .filter(c => !["rank", "name", "team", "position"].includes(c.key));

  stats = [...stats, ...extra];
}
  } else {
    // ===== ADVANCED =====
    if (category === "passing") {
      stats = [
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
        { key: "passing_interceptions", label: "INT", sortable: true },
        { key: "qbr", label: "QBR", sortable: true },
        { key: "passing_completion_pct", label: "Comp %", sortable: true },
        { key: "passing_completions", label: "Pass Comp", sortable: true },
        { key: "passing_attempts", label: "Pass Att", sortable: true },
        { key: "yards_per_pass_attempt", label: "Y/Pass", sortable: true },
        { key: "passing_yards_per_game", label: "Pass Yds/GM", sortable: true },
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "rushing_yards_per_game", label: "Rush Yds/GM", sortable: true },
        { key: "rushing_fumbles", label: "Rush Fum", sortable: true },
        { key: "rushing_fumbles_lost", label: "Rush Fum Lost", sortable: true },
        { key: "rushing_first_downs", label: "Rush 1D", sortable: true },
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
        { key: "receiving_targets", label: "Targets", sortable: true },
        { key: "receiving_yards_per_game", label: "Rec Y/G", sortable: true },
        { key: "receiving_first_downs", label: "Rec 1D", sortable: true },
        { key: "receiving_fumbles", label: "Rec Fum", sortable: true },
        { key: "receiving_fumbles_lost", label: "Rec Fum Lost", sortable: true },
        { key: "total_tackles", label: "Tackles", sortable: true },
      ];
    } else if (category === "rushing") {
      stats = [
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "rushing_yards_per_game", label: "Rush Yds/GM", sortable: true },
        { key: "rushing_fumbles", label: "Rush Fum", sortable: true },
        { key: "rushing_fumbles_lost", label: "Rush Fum Lost", sortable: true },
        { key: "rushing_first_downs", label: "Rush 1D", sortable: true },
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
        { key: "receiving_targets", label: "Targets", sortable: true },
        { key: "receiving_yards_per_game", label: "Rec Y/G", sortable: true },
        { key: "receiving_first_downs", label: "Rec 1D", sortable: true },
        { key: "receiving_fumbles", label: "Rec Fum", sortable: true },
        { key: "receiving_fumbles_lost", label: "Rec Fum Lost", sortable: true },
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
        { key: "passing_interceptions", label: "INT", sortable: true },
        { key: "qbr", label: "QBR", sortable: true },
        { key: "passing_completion_pct", label: "Comp %", sortable: true },
        { key: "passing_completions", label: "Pass Comp", sortable: true },
        { key: "passing_attempts", label: "Pass Att", sortable: true },
        { key: "total_tackles", label: "Tackles", sortable: true },
      ];
    } else if (category === "receiving") {
      stats = [
        { key: "receptions", label: "Rec", sortable: true },
        { key: "receiving_yards", label: "Rec Yds", sortable: true },
        { key: "receiving_touchdowns", label: "Rec TD", sortable: true },
        { key: "receiving_targets", label: "Targets", sortable: true },
        { key: "receiving_yards_per_game", label: "Rec Y/G", sortable: true },
        { key: "receiving_first_downs", label: "Rec 1D", sortable: true },
        { key: "receiving_fumbles", label: "Rec Fum", sortable: true },
        { key: "receiving_fumbles_lost", label: "Rec Fum Lost", sortable: true },
        { key: "rushing_attempts", label: "Rush Att", sortable: true },
        { key: "rushing_yards", label: "Rush Yds", sortable: true },
        { key: "yards_per_rush_attempt", label: "Y/A", sortable: true },
        { key: "rushing_touchdowns", label: "Rush TD", sortable: true },
        { key: "rushing_yards_per_game", label: "Rush Yds/GM", sortable: true },
        { key: "rushing_fumbles", label: "Rush Fum", sortable: true },
        { key: "rushing_fumbles_lost", label: "Rush Fum Lost", sortable: true },
        { key: "rushing_first_downs", label: "Rush 1D", sortable: true },
        { key: "passing_yards", label: "Pass Yds", sortable: true },
        { key: "passing_touchdowns", label: "Pass TD", sortable: true },
        { key: "passing_interceptions", label: "INT", sortable: true },
        { key: "qbr", label: "QBR", sortable: true },
        { key: "passing_completion_pct", label: "Comp %", sortable: true },
        { key: "passing_completions", label: "Pass Comp", sortable: true },
        { key: "passing_attempts", label: "Pass Att", sortable: true },
        { key: "total_tackles", label: "Tackles", sortable: true },
      ];
    } else if (category === "defense") {
      stats = [
        { key: "total_tackles", label: "Tackles", sortable: true },
        { key: "solo_tackles", label: "Solo", sortable: true },
        { key: "assist_tackles", label: "Assisted", sortable: true },
        { key: "defensive_sacks", label: "Sacks", sortable: true },
        { key: "defensive_sack_yards", label: "Sack Yds", sortable: true },
        { key: "fumbles_forced", label: "Forced Fum", sortable: true },
        { key: "fumbles_recovered", label: "Fum Rec", sortable: true },
        { key: "fumbles_touchdowns", label: "Fum TDs", sortable: true },
        { key: "defensive_interceptions", label: "INT", sortable: true },
        { key: "interception_touchdowns", label: "INT TDs", sortable: true },
      ];
    } else if (category === "kicking") {
      stats = [
        { key: "field_goals_made", label: "FG Made", sortable: true },
        { key: "field_goal_attempts", label: "FG Att", sortable: true },
        { key: "field_goal_pct", label: "FG %", sortable: true },
        { key: "field_goals_made_1_19", label: "FGM 1-19", sortable: true },
        { key: "field_goals_made_20_29", label: "FGM 20-29", sortable: true },
        { key: "field_goals_made_30_39", label: "FGM 30-39", sortable: true },
        { key: "field_goals_made_40_49", label: "FGM 40-49", sortable: true },
        { key: "field_goals_made_50", label: "FGM 50+", sortable: true },
        { key: "field_goals_attempts_1_19", label: "FGA 1-19", sortable: true },
        { key: "field_goals_attempts_20_29", label: "FGA 20-29", sortable: true },
        { key: "field_goals_attempts_30_39", label: "FGA 30-39", sortable: true },
        { key: "field_goals_attempts_40_49", label: "FGA 40-49", sortable: true },
        { key: "field_goals_attempts_50", label: "FGA 50+", sortable: true },
      ];
    }
  }

  return [...base, ...stats];
};

  const columns = getColumns(selectedCategory, showAdvanced);
  const rankedPlayers = filteredPlayers.map((p, index) => ({ ...p, rank: index + 1 }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-white">
        Loading Stats Center...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR — paste your existing navbar JSX here unchanged */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
  <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
    {/* Left: Logo */}
    <Link to="/" className="flex items-center gap-3">
      <img
        src="/images/HOFLogoSquare.png"
        alt="HOF Logo"
        className="w-12 h-12 object-contain"
      />
      <div>
        <div className="text-2xl font-black tracking-tighter">
          HELMETS OFF
        </div>
        <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
      </div>
    </Link>

    <div className="flex-1 hidden md:block"></div>

    {/* Right side */}
    <div className="flex items-center gap-4">
      {/* Collapsible Search */}
      <div className="relative" ref={navbarContainerRef}>
        {!searchExpanded ? (
          <button
            onClick={() => {
              setSearchExpanded(true);
              setTimeout(() => navbarInputRef.current?.focus(), 50);
            }}
            className="p-2 text-zinc-400 hover:text-white transition"
            aria-label="Search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        ) : (
          <div className="relative w-64 sm:w-72">
            <input
              ref={navbarInputRef}
              type="text"
              value={navbarSearchTerm}
              onChange={(e) => setNavbarSearchTerm(e.target.value)}
              placeholder="Search players..."
              className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
              autoFocus
            />
            <span className="absolute left-4 top-3.5 text-zinc-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>

            {navbarResults.length > 0 && (
              <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
                {navbarResults.map((item, index) => (
                  <Link
                    key={`${item.type}-${item.bdl_id || item.id}`}
                    to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                    className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                    onClick={() => {
                      setNavbarSearchTerm("");
                      setNavbarResults([]);
                      setSearchExpanded(false);
                    }}
                  >
                    {item.type === "team" ? (
                      <>
                        <span className="font-medium">{item.name}</span>
                        <span className="text-zinc-500 text-sm"> • Team</span>
                      </>
                    ) : (
                      <>
                        {item.first_name} {item.last_name} • {item.position_abbreviation}
                      </>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop Nav Links */}
      <div className="hidden md:flex items-center gap-8 text-sm font-medium">
        {/* TEAMS */}
        <div className="relative" ref={teamsRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
            className="hover:text-red-400 transition"
          >
            TEAMS
          </button>
          {openDropdown === "teams" && (
            <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
              <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
              <Link to="/Standings" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
              <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
            </div>
          )}
        </div>

        {/* LEADERS */}
        <div className="relative" ref={leadersRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
            className="hover:text-red-400 transition"
          >
            LEADERS
          </button>
          {openDropdown === "leaders" && (
            <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
              <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
              <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
              <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
              <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
            </div>
          )}
        </div>

        {/* PLAYERS */}
        <div className="relative" ref={playersRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
            className="hover:text-red-400 transition"
          >
            PLAYERS
          </button>
          {openDropdown === "players" && (
            <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
              <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
              <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Hamburger */}
      <button
        className="md:hidden p-2 text-zinc-400 hover:text-white"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Menu"
      >
        {mobileMenuOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>
    </div>
  </div>

  {/* Mobile Menu */}
  {mobileMenuOpen && (
    <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
      {/* TEAMS */}
      <div className="border-b border-zinc-800">
        <button
          onClick={() => setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")}
          className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
        >
          TEAMS
          <span className="text-zinc-500 text-sm">{mobileOpenSection === "teams" ? "−" : "+"}</span>
        </button>
        {mobileOpenSection === "teams" && (
          <div className="pb-4 space-y-3 pl-3">
            <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>All Teams</Link>
            <Link to="/Standings" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Standings</Link>
            <Link to="/Schedule" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Schedule</Link>
          </div>
        )}
      </div>

      {/* LEADERS */}
      <div className="border-b border-zinc-800">
        <button
          onClick={() => setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")}
          className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
        >
          LEADERS
          <span className="text-zinc-500 text-sm">{mobileOpenSection === "leaders" ? "−" : "+"}</span>
        </button>
        {mobileOpenSection === "leaders" && (
          <div className="pb-4 space-y-3 pl-3">
            <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Season Leaders</Link>
            <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Passing</Link>
            <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Rushing</Link>
            <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Receiving</Link>
          </div>
        )}
      </div>

      {/* PLAYERS */}
      <div>
        <button
          onClick={() => setMobileOpenSection(mobileOpenSection === "players" ? null : "players")}
          className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
        >
          PLAYERS
          <span className="text-zinc-500 text-sm">{mobileOpenSection === "players" ? "−" : "+"}</span>
        </button>
        {mobileOpenSection === "players" && (
          <div className="pb-4 space-y-3 pl-3">
            <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Player Stats</Link>
            <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>Player Comparison</Link>
          </div>
        )}
      </div>
    </div>
  )}
</nav>

      <div className="pt-8 pb-12 max-w-7xl mx-auto px-6">
        
        <h1 className="text-6xl font-black text-center mb-8 tracking-tighter">
          PLAYER STATS CENTER
        </h1>

        {/* Category buttons (Standings-style) */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              className={`px-6 py-3 rounded-xl font-bold text-sm tracking-wider transition ${
                selectedCategory === cat.key
                  ? "bg-red-600 text-white"
                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
              }`}
            >
              {cat.label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Secondary filters */}
        <div className="flex flex-wrap gap-4 justify-center mb-10">
          <select
            value={selectedPosition}
            onChange={(e) => setSelectedPosition(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-3 text-sm focus:outline-none focus:border-red-500"
          >
            {POSITIONS_BY_CATEGORY[selectedCategory].map((pos) => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </select>

          <select
            value={selectedConference}
            onChange={(e) => setSelectedConference(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-3 text-sm focus:outline-none focus:border-red-500"
          >
            <option value="All">All Conferences</option>
            <option value="AFC">AFC</option>
            <option value="NFC">NFC</option>
          </select>

          {selectedCategory === "fantasy" && (
          <select
             value={selectedFantasyFormat}
             onChange={(e) => setSelectedFantasyFormat(e.target.value)}
             className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-3 text-sm focus:outline-none focus:border-red-500"
          >
           <option value="full_ppr">PPR</option>
           <option value="half_ppr">Half PPR</option>
            <option value="standard">Standard</option>
           </select>
          )}    

          <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(Number(e.target.value))}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-3 text-sm focus:outline-none focus:border-red-500"
          >
              <option value={2026}>2026</option>
              <option value={2025}>2025</option>
          </select>

          <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              disabled={selectedCategory === "fantasy"}
              className={`px-6 py-3 rounded-xl font-bold text-sm transition ${
              showAdvanced && selectedCategory !== "fantasy"
              ? "bg-red-600 text-white"
              : selectedCategory === "fantasy"
              ? "bg-zinc-800 text-zinc-600 cursor-not-allowed"
              : "bg-zinc-800 hover:bg-zinc-700"
            }`}
          >
          {showAdvanced && selectedCategory !== "fantasy" ? "Hide Advanced" : "Show Advanced"}
          </button>
        </div>

        {/* Table */}
      <div className="bg-zinc-900 rounded-3xl overflow-hidden shadow-2xl border border-zinc-700">
            <div className="overflow-x-auto max-h-[75vh] overflow-y-auto">
              <table className="w-full table-fixed text-sm">
                  <thead className="sticky top-0 bg-zinc-950 z-10">
                    <tr className="border-b border-zinc-700">
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          onClick={() => col.sortable && handleSort(col.key)}
                          className={`py-2.5 px-2 font-bold cursor-pointer hover:text-red-400 whitespace-nowrap text-xs ${
                            col.key === "name"
                              ? "text-left w-32 max-w-[8rem]"
                              : "text-center min-w-[4.5rem] max-w-[6.5rem]"
                          }`}
                        >
                          {col.label}{" "}
                          {sortConfig.key === col.key && (sortConfig.direction === "desc" ? "↓" : "↑")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rankedPlayers.map((p) => (
                      <tr key={p.player_id} className="border-b border-zinc-800 hover:bg-zinc-800/70 transition">
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={`py-2 px-2 text-xs ${
                              col.key === "name"
                                ? "w-32 max-w-[8rem] truncate"
                                : "text-center min-w-[4.5rem] max-w-[6.5rem]"
                            }`}
                          >
                            {col.key === "name" ? (
                              <Link to={`/player/${p.player_id}`} className="hover:text-red-400 font-medium">
                                {p.name}
                              </Link>
                            ) : (
                              formatValue(p[col.key])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
          </div>
      </div>
      </div>

      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default PlayerStatsPage;